/**
 * BatchSelector
 *
 * Picks which batch instance of a run graph to render. Batched runs clone the
 * whole graph per instance with colliding ids and identical positions, so only
 * one instance can be shown at a time — see graph/domain/batches.
 *
 * Renders nothing when the graph is not batched, so unbatched runs are visually
 * unchanged.
 *
 * Deliberately dependency-free (native <select>, CSS module) rather than MUI:
 * this ships inside the chrome-free iframe viewer, which hand-rolls its own
 * spinner and error states for the same reason.
 */

import React from 'react';

import type { BatchDescriptor } from '@/graph';
import styles from './BatchSelector.module.css';

interface BatchSelectorProps {
  batches: BatchDescriptor[];
  value: number | null;
  onChange: (cloneNumber: number) => void;
  disabled?: boolean;
}

export const BatchSelector: React.FC<BatchSelectorProps> = ({
  batches,
  value,
  onChange,
  disabled = false,
}) => {
  if (batches.length <= 1) return null;

  return (
    <div className={styles.wrapper}>
      <span className={styles.label}>Batch</span>
      <select
        className={styles.select}
        value={value ?? batches[0].cloneNumber}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label="Select batch instance"
      >
        {batches.map((batch) => (
          <option key={batch.cloneNumber} value={batch.cloneNumber}>
            {batch.label}
          </option>
        ))}
      </select>
    </div>
  );
};
